import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import RequestStatus from 'components/RequestStatus'
import type { ApiApplicationAdditionalInfo, ApiApplicationDetails } from 'types/Application'
import { CardHeader, Typography, Grid, Alert, AlertTitle, Link, Paper } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { axiosPatch } from 'connectors/axiosPatch'
import { axiosPost, SendgridError } from 'connectors/axiosPost'
import { StatusTypes, applicationStatuses } from 'pages/Applications/applicationStatuses'
import type { UpdateStatusData } from 'pages/Applications/Applications.types'
import StatusButtons from 'components/StatusButtons'
import { useAppContext } from 'context/AppContext/AppContext'
import { useAlert } from 'context/AlertContext'
import useQueryGet from 'hooks/useQueryGet'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import { useAuthContext } from 'context/AuthContext/AuthContext'
import { generateQueryParams, readQueryParams } from 'utils/generateQueryParams'
import ReportOutlinedIcon from '@mui/icons-material/ReportOutlined'
import Chip from '@mui/material/Chip'
import { History } from './History'
import { Images } from './Images'
import CircularProgress from '@mui/material/CircularProgress'
import PageLoader from 'components/PageLoader'
import { DetailsRow } from './features/details-row'
import { ApplicationData } from './features/application-data'
import { TechnicalData } from './features/technical-data'
import { StatusChip } from './features/status-chip'
import { AxiosError } from 'axios'

const ApplicationDetails = (): JSX.Element => {
  const [showHistory, setShowHistory] = useState(false)
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const { token, refreshToken } = useAuthContext()
  const { changeMessage } = useAlert()
  const { markets, isDarkMode } = useAppContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data, isLoading } = useQueryGet<ApiApplicationDetails>({
    queryKey: ['application-details', params.id],
    endpoint: `applications/${params.id ?? ''}`,
    options: { params: searchParams },
    refetchDeps: [searchParams],
  })

  const extraApplicationInfo = useQueryGet<ApiApplicationAdditionalInfo>({
    queryKey: ['application-extra-info', params.id],
    endpoint: `applications/additional-data/${params.id ?? ''}`,
    options: { params: searchParams },
    refetchDeps: [searchParams],
  })

  const searchParamsObject = useMemo(() => readQueryParams(searchParams), [searchParams])

  const {
    mutate: updateStatusMutate,
    data: updateStatusResponse,
    reset,
    isLoading: isUpdating,
  } = useMutation(
    async ({ id, newStatusId }: UpdateStatusData) =>
      await axiosPatch<{ affected: number }>(token, refreshToken, `applications/status/${id}`, { status: newStatusId }),
    {
      onSuccess: (response, { newStatusId }) => {
        if (data !== undefined && response !== undefined && !('error' in response)) {
          queryClient.setQueryData(['application-details', params.id], { ...data, application_status: newStatusId })
        }
      },
    }
  )

  useEffect(() => {
    if (
      typeof updateStatusResponse !== 'string' &&
      updateStatusResponse !== undefined &&
      'error' in updateStatusResponse
    ) {
      changeMessage(updateStatusResponse?.error?.data?.message, 'error', () => {
        reset()
      })
    }
    if (
      typeof updateStatusResponse !== 'string' &&
      updateStatusResponse !== undefined &&
      !('error' in updateStatusResponse) &&
      updateStatusResponse.affected > 0
    ) {
      changeMessage(t('common.success'), 'success', () => {
        reset()
      })
    }
  }, [updateStatusResponse])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutate: resendVerification, isLoading: isResending } = useMutation(
    async ({ id, email }: { id: string; email: string }) =>
      await axiosPost(token, refreshToken, 'applications/send-verification-email', {
        id,
        email,
      }),
    {
      onSettled: data => {
        const resultData = data as { error: AxiosError<SendgridError>['response'] }
        if (resultData?.error?.data.statusCode === 400) {
          if (Array.isArray(resultData.error.data.message)) {
            resultData.error.data.message.forEach(item => {
              changeMessage(t(item.message), 'error')
            })
          }
        }
      },
    }
  )

  if (data === null || data === undefined || 'error' in data) {
    return <RequestStatus data={data} isLoading={isLoading} />
  }

  const renderNav = (): JSX.Element => (
    <CardHeader
      sx={{
        padding: 0,
        paddingBottom: 2,
        position: 'sticky',
        top: -32,
        py: 1,
        backgroundColor: 'inherit',
        zIndex: 999,
      }}
      title={
        <Box display="flex" gap={3} alignItems="center">
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={() => {
              navigate(`/applications?${searchParams.toString()}`)
            }}
            startIcon={<ChevronLeft />}
          >
            {t('common.backListText')}
          </Button>

          <Typography variant="h5" fontWeight={500} component="h1">
            #{data.application_id} {data.application_fullName} / {data.application_email}
          </Typography>
        </Box>
      }
      action={
        <>
          <Button
            variant="outlined"
            color="secondary"
            disabled={
              extraApplicationInfo.isLoading ||
              (extraApplicationInfo.data != null &&
                !('error' in extraApplicationInfo.data) &&
                !extraApplicationInfo.data.prevApplicationId)
            }
            onClick={() => {
              if (
                extraApplicationInfo.data != null &&
                !('error' in extraApplicationInfo.data) &&
                extraApplicationInfo.data.prevApplicationId
              ) {
                navigate(`/applications/${extraApplicationInfo.data.prevApplicationId}?${searchParams.toString()}`)
              }
            }}
          >
            {t('common.prev')} {extraApplicationInfo.isLoading ? <CircularProgress /> : null}
          </Button>
          <Button
            variant="contained"
            disabled={
              extraApplicationInfo.isLoading ||
              (extraApplicationInfo.data != null &&
                !('error' in extraApplicationInfo.data) &&
                !extraApplicationInfo.data.nextApplicationId)
            }
            onClick={() => {
              if (
                extraApplicationInfo.data != null &&
                !('error' in extraApplicationInfo.data) &&
                extraApplicationInfo.data.nextApplicationId
              ) {
                navigate(`/applications/${extraApplicationInfo.data.nextApplicationId}?${searchParams.toString()}`)
              }
            }}
            sx={{ marginLeft: 1 }}
          >
            {t('common.next')} {extraApplicationInfo.isLoading ? <CircularProgress /> : null}
          </Button>
        </>
      }
    />
  )

  const handleDeleteFilter = (name: string, value: string): void => {
    const selectedObject = searchParamsObject[name]

    if (selectedObject instanceof Array) {
      const { [name]: test, ...rest } = searchParamsObject
      setSearchParams(generateQueryParams({ ...rest, [name]: selectedObject.filter(e => e !== value) }))
    } else {
      const { [name]: test, ...rest } = searchParamsObject
      setSearchParams(generateQueryParams(rest))
    }
  }

  const renderFilters = (): ReactNode => {
    const filters: Record<string, Record<string, string>> = {
      status: Object.fromEntries(applicationStatuses.map((name, idx) => [idx, name.value])),
      marketId: markets !== undefined ? Object.fromEntries(markets.data.map(el => [el.id.toString(), el.name])) : {},
    }

    const checkValue = (name: keyof typeof filters | string, value: string): string =>
      name in filters && value in filters[name] ? filters[name][value] : value

    return Object.entries(searchParamsObject).map(([name, values], idx) => {
      if (values instanceof Array) {
        return values.map(value => (
          <Chip
            sx={{ mr: 1 }}
            key={value}
            label={`${name}: ${checkValue(name, value)}`}
            onDelete={() => {
              handleDeleteFilter(name, value)
            }}
          />
        ))
      } else {
        return (
          <Chip
            sx={{ mr: 1 }}
            key={idx}
            label={`${name}: ${checkValue(name, values.toString())}`}
            onDelete={() => {
              handleDeleteFilter(name, values.toString())
            }}
          />
        )
      }
    })
  }

  return (
    <>
      <Paper sx={{ p: 4 }}>
        {renderNav()}
        <Box my={2}>{renderFilters()}</Box>
        <Grid container spacing={4} mt={2}>
          <Grid item xs={12} lg={8} display="flex" flexDirection="column" gap={4}>
            <DetailsRow title="Application status" description="Current status in the consumer application's journey">
              <StatusChip status={data.application_status} />
              {/* <IconChip
                icon={applicationStatuses[data.application_status].icon}
                color={applicationStatuses[data.application_status].color}
                label={applicationStatuses[data.application_status].value ?? 'N/A'}
              /> */}

              {data.application_status === StatusTypes.EmailVerification ? (
                <Button
                  onClick={() => {
                    resendVerification({ id: data.application_id.toString(), email: data.application_email })
                  }}
                  variant="contained"
                  disabled={isResending}
                  size="small"
                  sx={{ mt: 0.5 }}
                >
                  {t('application.resendVerification')}
                </Button>
              ) : null}
            </DetailsRow>

            <ApplicationData data={data} />

            <TechnicalData data={data} />
          </Grid>

          <Grid item xs={12} lg={4}>
            <Typography variant="h6" component="h2" mb={1}>
              {t('application.attachments')}
            </Typography>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minHeight: 600,
                  overflow: 'hidden',
                }}
              >
                {extraApplicationInfo.data !== undefined &&
                !('error' in extraApplicationInfo.data) &&
                extraApplicationInfo.data?.files !== undefined &&
                extraApplicationInfo.data.files.length > 0 ? (
                  <Images files={extraApplicationInfo.data.files} />
                ) : (
                  t('application.noFiles')
                )}

                {data.duplicatedImages !== undefined && data.duplicatedImages.length > 0 ? (
                  <Alert
                    severity="warning"
                    sx={{ display: 'flex', mb: 2 }}
                    icon={<ReportOutlinedIcon fontSize="large" />}
                  >
                    <AlertTitle sx={{ fontSize: '18px', lineHeight: '24px', mb: 0 }}>
                      {t('dialogs.similarGraphics')}
                    </AlertTitle>
                    <List sx={{ width: '100%' }}>
                      {data.duplicatedImages.map(({ application }, idx) => (
                        <ListItem key={idx}>
                          <Link href={`/applications/${application.id}`}>
                            {application.id}: {application.email}
                          </Link>
                        </ListItem>
                      ))}
                    </List>
                  </Alert>
                ) : null}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={3}>
          <Grid item xs={12}>
            {showHistory ? (
              <History applicationId={params.id} marketCode={data.market_code} />
            ) : (
              <Button
                variant="contained"
                onClick={() => {
                  setShowHistory(true)
                }}
              >
                {t('application.applicationHistory')}
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* {renderNav()} */}
      {data.application_status !== StatusTypes.Deleted ? (
        <Box
          sx={{
            position: 'sticky',
            bottom: -24,
            paddingBlock: 3,
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            backgroundColor: isDarkMode ? 'background.paper' : 'grey.50',
          }}
        >
          <StatusButtons
            flow={data.market_flow}
            prize={data.market_prize}
            receiptValidation={data.receiptValidation}
            id={data.application_id}
            status={data.application_status}
            langId={data.application_marketLanguageId}
            updateHandler={updateStatusMutate}
          />
          {/* <Button variant='contained' color='error' onClick={() => { updateStatusMutate({ newStatusId: 7, id: data.application_id }) }} startIcon={<DeleteOutlinedIcon/>}>{t('common.delete')}</Button> */}
        </Box>
      ) : null}

      {isUpdating && <PageLoader />}
    </>
  )
}

export default ApplicationDetails
